import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { AccountInfo, SilentRequest, InteractionStatus } from '@azure/msal-browser';
import { config } from '../config';

// Enhanced debugging
const debugAuth = (message: string, data?: any) => {
  console.log(`🔐 [AUTH] ${message}`, data || '');
};

// Auth state interface
interface AuthState {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  isLoading: boolean;
  error: string | null;
}

// Azure B2C authentication hook
export const useAuth = () => {
  const { instance, accounts, inProgress } = useMsal();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
    error: null
  });

  // Debug MSAL state changes
  useEffect(() => {
    debugAuth('MSAL State Change', {
      accountsLength: accounts.length,
      inProgress,
      accounts: accounts.map(acc => ({ username: acc.username, name: acc.name }))
    });
  }, [accounts, inProgress]);

  // Check authentication state
  useEffect(() => {
    const checkAuth = async () => {
      debugAuth('Starting authentication check');

      // Wait until MSAL has finished any in-progress operation before deciding the
      // auth state. While inProgress is anything other than 'none' — including the
      // initial 'startup' and 'handleRedirect' phases on first page load — accounts
      // may not be populated yet. Concluding "unauthenticated" during that window is
      // what causes the LandingPage to briefly flash before the session is restored.
      if (inProgress !== InteractionStatus.None) {
        debugAuth('MSAL operation in progress, waiting...', inProgress);
        setAuthState(prev => ({ ...prev, isLoading: true }));
        return;
      }

      if (accounts.length === 0) {
        debugAuth('No accounts found, user not authenticated');
        setAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
          error: null
        });
        return;
      }

      try {
        debugAuth('Accounts found, attempting silent token acquisition');
        const account = accounts[0];
        debugAuth('Using account', {
          username: account.username,
          name: account.name,
          localAccountId: account.localAccountId
        });
        
        const tokenRequest: SilentRequest = {
          ...config.tokenRequest,
          account: account,
        };

        debugAuth('Token request', tokenRequest);
        
        const response = await instance.acquireTokenSilent(tokenRequest);
        
        debugAuth('Token acquisition successful', {
          accessToken: response.accessToken ? 'Present' : 'Missing',
          expiresOn: response.expiresOn,
          scopes: response.scopes
        });
        
        // Store token for API calls
        localStorage.setItem('msal.token', response.accessToken);

        setAuthState({
          isAuthenticated: true,
          user: account,
          isLoading: false,
          error: null
        });

      } catch (error: any) {
        debugAuth('Silent token acquisition failed', error);
        
        // Clear any stored tokens
        localStorage.removeItem('msal.token');
        
        // Check if this is an interaction required error (normal for expired sessions)
        if (error.errorCode === 'interaction_required' || 
            error.name === 'InteractionRequiredAuthError' ||
            error.message?.includes('interaction_required')) {
          debugAuth('Interaction required - user needs to sign in again');
          
          // Clear the account cache since the session is expired
          try {
            await instance.clearCache();
            debugAuth('Cleared MSAL cache due to expired session');
          } catch (clearError) {
            debugAuth('Failed to clear cache', clearError);
          }
          
          // This is normal, just treat as not authenticated
          setAuthState({
            isAuthenticated: false,
            user: null,
            isLoading: false,
            error: null // Don't show this as an error
          });
        } else {
          // This is an actual error that should be displayed
          setAuthState({
            isAuthenticated: false,
            user: null,
            isLoading: false,
            error: `Authentication failed: ${error}`
          });
        }
      }
    };

    checkAuth();
  }, [instance, accounts, inProgress]);

  // Login function
  const login = useCallback(async () => {
    debugAuth('Login initiated');
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Validate configuration before attempting login
      const clientId = config.msalConfig.auth.clientId;
      const authority = config.msalConfig.auth.authority;
      
      if (!clientId || clientId === 'your-client-id') {
        throw new Error('Invalid client ID configuration. Please check your .env file.');
      }
      
      if (!authority || !authority.includes('b2clogin.com')) {
        throw new Error('Invalid authority URL. Please check your B2C configuration.');
      }
      
      debugAuth('Pre-login validation passed');
      debugAuth('Login request config', config.loginRequest);
      
      await instance.loginRedirect({
        ...config.loginRequest,
        extraScopesToConsent: config.loginRequest.scopes
      });
      
      debugAuth('Login redirect initiated');
    } catch (error: any) {
      debugAuth('Login failed', error);
      
      let errorMessage = 'Login failed';
      
      if (error.errorCode) {
        switch (error.errorCode) {
          case 'user_cancelled':
            errorMessage = 'Login was cancelled by user';
            break;
          case 'access_denied':
            errorMessage = 'Access denied. Please check your permissions.';
            break;
          case 'server_error':
            errorMessage = 'Server error. Please try again later.';
            break;
          case 'invalid_request':
            errorMessage = 'Invalid request. Please check your B2C configuration.';
            break;
          case 'invalid_client':
            errorMessage = 'Invalid client configuration. Please check your client ID.';
            break;
          case 'invalid_resource':
            errorMessage = 'Invalid resource. Please check your scopes configuration.';
            break;
          case 'invalid_scope':
            errorMessage = 'Invalid scope. Please check your scope configuration.';
            break;
          default:
            errorMessage = `Login failed: ${error.errorCode} - ${error.errorMessage || error.message}`;
        }
      } else {
        errorMessage = `Login failed: ${error.message || error}`;
      }
      
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
    }
  }, [instance]);

  // Logout function
  const logout = useCallback(async () => {
    debugAuth('Logout initiated');
    setAuthState(prev => ({ ...prev, isLoading: true }));

    try {
      // Clear stored tokens
      localStorage.removeItem('msal.token');
      
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin
      });
      debugAuth('Logout redirect initiated');
    } catch (error) {
      debugAuth('Logout failed', error);
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: `Logout failed: ${error}`
      }));
    }
  }, [instance]);

  // Get fresh token function
  const getToken = useCallback(async (): Promise<string | null> => {
    if (!authState.isAuthenticated || !authState.user) {
      debugAuth('Cannot get token - user not authenticated');
      return null;
    }

    try {
      debugAuth('Getting fresh token');
      const response = await instance.acquireTokenSilent({
        ...config.tokenRequest,
        account: authState.user,
      });

      localStorage.setItem('msal.token', response.accessToken);
      debugAuth('Fresh token acquired successfully');
      return response.accessToken;
      
    } catch (error) {
      debugAuth('Failed to get fresh token', error);
      return null;
    }
  }, [instance, authState.user, authState.isAuthenticated]);

  return {
    ...authState,
    login,
    logout,
    getToken
  };
};
